import axios from 'axios';
import { API_BASE_URL, getAuthToken, saveSession } from '../config/constants';

/**
 * Cliente axios SIN interceptores para endpoints públicos de auth
 */
const authClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json'
    }
});

/**
 * Servicio para operaciones de autenticación
 */
export const authService = {
    /**
     * Iniciar sesión con email y password
     * POST /login
     */
    login: async (email, password) => {
        const encodedPassword = btoa(password);

        const response = await authClient.post('/login', {
            email: email,
            password: encodedPassword
        });

        // Guardar sesión usando el helper de constants.js
        if (response.data?.session && response.data?.user) {
            saveSession(response.data.session, response.data.user);
        }

        return response.data;
    },

    /**
     * Registrar nuevo usuario
     * POST /register
     */
    register: async (userData) => {
        const response = await authClient.post('/register', userData);

        // Después de registrar, se guarda la sesión automáticamente
        if (response.data?.session && response.data?.user) {
            saveSession(response.data.session, response.data.user);
        }

        return response.data;
    },

    /**
     * Obtener URL de autorización de Google
     * GET /auth/google
     */
    getGoogleAuthUrl: async (channel = process.env.REACT_APP_AUTH_CHANNEL) => {
        const response = await authClient.get('/auth/google', {
            params: {
                ...(channel ? { channel } : {}),
                prompt: 'select_account'
            }
        });

        return response.data.authorize_url;
    },

    /**
     * Procesar callback de Google OAuth
     * POST /auth/google/callback?code...&state=...
     */
    googleCallback: async (code, state = null, channel = process.env.REACT_APP_AUTH_CHANNEL) => {
        // Construir la URL con query params
        const params = new URLSearchParams({ code });
        if (state) params.append('state', state);
        if (channel) params.append('channel', channel);

        const response = await authClient.post(`/auth/google/callback?${params.toString()}`);

        // Guardar la sesión
        if (response.data?.session && response.data?.user) {
            saveSession(response.data.session, response.data.user);
        }

        return response.data;
    },

    /**
     * Refrescar token de acceso
     * POST /auth/refresh
     * (Ya se maneja en el interceptor en api.js, pero disponible por si se necesita manual)
     */
    refreshToken: async (refreshToken) => {
        const response = await authClient.post('/auth/refresh', {
            refresh_token: refreshToken
        });

        return response.data;
    },

    /**
     * Manejar cierre de sesión (logout)
     * POST /auth/logout
     */
    logout: async (accessToken = null) => {
        const resolvedAccessToken = accessToken || getAuthToken();

        if (!resolvedAccessToken) {
            throw new Error('No hay access_token para el logout');
        }

        const response = await authClient.post(
            '/auth/logout',
            {
                refresh_token: resolvedAccessToken
            },
            {
                headers: {
                    Authorization: `Bearer ${resolvedAccessToken}`
                }
            }
        );

        return response.data;
    }
};